import type {
  MonitoringActivityEvent,
  MonitoringAnalyticsRealtime,
  MonitoringLocationCityBreakdown,
  MonitoringLocationRegionBreakdown,
  MonitoringMapFocus,
  MonitoringMapPoint,
  MonitoringMapPointKind,
} from '../types/monitoring';
import { getApiOrigin } from '../config/environment';

export function resolveMonitoringImageUrl(imageUrl?: string | null): string | null {
  if (!imageUrl) return null;
  const trimmed = imageUrl.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const origin = getApiOrigin();
  return `${origin}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

export const GA4_VISITOR_COLOR = '#4285F4';

export function kindLabel(kind: MonitoringMapPointKind): string {
  switch (kind) {
    case 'lost':
      return 'Perdu';
    case 'found':
      return 'Trouvé';
    case 'matching':
      return 'Matching';
    case 'ga4':
      return 'Visiteurs web';
    default:
      return kind;
  }
}

export function announcementTypeLabel(type?: string | null): string {
  if (type === 'found') return 'Trouvé';
  if (type === 'lost') return 'Perdu';
  return type ?? '';
}

export function kindColor(
  kind: MonitoringMapPointKind,
  lostColor: string,
  foundColor: string,
  matchingColor = '#0ea5e9'
): string {
  if (kind === 'lost') return lostColor;
  if (kind === 'found') return foundColor;
  if (kind === 'ga4') return GA4_VISITOR_COLOR;
  return matchingColor;
}

export function ga4AnalyticsToMapPoints(
  analytics: MonitoringAnalyticsRealtime | null | undefined
): MonitoringMapPoint[] {
  if (!analytics?.configured || !analytics.cities?.length) {
    return [];
  }

  return analytics.cities
    .filter(
      (row) =>
        typeof row.latitude === 'number' &&
        typeof row.longitude === 'number' &&
        Number.isFinite(row.latitude) &&
        Number.isFinite(row.longitude)
    )
    .map((row) => {
      const label =
        row.active_users === 1
          ? `${row.city} — 1 visiteur`
          : `${row.city} — ${row.active_users} visiteurs`;

      return {
        id: `ga4-${row.city}-${row.country}`.replace(/\s+/g, '-').toLowerCase(),
        kind: 'ga4' as const,
        announcement_id: null,
        match_id: null,
        latitude: row.latitude as number,
        longitude: row.longitude as number,
        label,
        description: 'Visiteurs web GA4 (temps réel, cookies analytics acceptés)',
        city: row.city,
        active_users: row.active_users,
        created_at: analytics.generated_at,
      };
    });
}

export function formatMonitoringDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function activityEventKey(event: { type: string; at: string | null }, index: number): string {
  return `${event.type}-${event.at ?? 'na'}-${index}`;
}

export function resolveMapPointIdFromEvent(event: {
  type: string;
  meta?: Record<string, unknown>;
}): string | null {
  const fromMeta = event.meta?.map_point_id;
  if (typeof fromMeta === 'string' && fromMeta.trim() !== '') {
    return fromMeta;
  }
  if (event.type === 'announcement_created' && event.meta?.announcement_id != null) {
    return `announcement-${event.meta.announcement_id}`;
  }
  if (event.type === 'matching_created' && event.meta?.match_id != null) {
    return `match-${event.meta.match_id}`;
  }
  return null;
}

export function findMapPointById(
  points: MonitoringMapPoint[],
  pointId: string | null | undefined
): MonitoringMapPoint | null {
  if (!pointId) return null;
  return points.find((point) => point.id === pointId) ?? null;
}

export function hasValidTargetCoordinates(point: MonitoringMapPoint): boolean {
  return (
    point.target_latitude != null &&
    point.target_longitude != null &&
    Number.isFinite(point.target_latitude) &&
    Number.isFinite(point.target_longitude)
  );
}

export function resolveMapFocusFromEvent(event: MonitoringActivityEvent): MonitoringMapFocus | null {
  const meta = event.meta ?? {};
  const latitude = Number(meta.latitude);
  const longitude = Number(meta.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const targetLatitude =
    meta.target_latitude != null && Number.isFinite(Number(meta.target_latitude))
      ? Number(meta.target_latitude)
      : null;
  const targetLongitude =
    meta.target_longitude != null && Number.isFinite(Number(meta.target_longitude))
      ? Number(meta.target_longitude)
      : null;

  return {
    pointId: resolveMapPointIdFromEvent(event),
    latitude,
    longitude,
    targetLatitude,
    targetLongitude,
  };
}

export function resolveMapFocusFromPoint(point: MonitoringMapPoint): MonitoringMapFocus {
  return {
    pointId: point.id,
    latitude: point.latitude,
    longitude: point.longitude,
    targetLatitude: point.target_latitude ?? null,
    targetLongitude: point.target_longitude ?? null,
  };
}

export function resolveMapFocusFromBounds(bounds: [[number, number], [number, number]]): MonitoringMapFocus {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;

  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
    bounds,
  };
}

export function resolveMapFocusFromRegion(region: MonitoringLocationRegionBreakdown): MonitoringMapFocus {
  return resolveMapFocusFromBounds(region.bounds);
}

export function resolveMapFocusFromCity(city: MonitoringLocationCityBreakdown): MonitoringMapFocus {
  return resolveMapFocusFromBounds(city.bounds);
}

export function filterMapPointsByLocation(
  points: MonitoringMapPoint[],
  selection: { region: string; city?: string | null }
): MonitoringMapPoint[] {
  return points.filter((point) => {
    if ((point.region ?? 'Région non renseignée') !== selection.region) {
      return false;
    }
    if (selection.city) {
      return (point.city ?? 'Ville non renseignée') === selection.city;
    }
    return true;
  });
}
